import { css } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Row,
  AvatarIcon,
  AvatarIcons,
  Space,
  Text,
  IconButton,
  useDisclosure,
  ActionModal,
  colors,
  ITableProps,
} from 'common-components';
import { ColumnsType } from 'antd/lib/table';
import { useNavigate, useParams } from 'react-router';
import { routes } from '@navigation/route-declarations';
import { PresetTag } from '@components/display/preset-tag';
import { useAppTranslation } from '@hooks';
import { UserCertificationState, getWirkUrl } from '@definitions';
import { formatPercent, getStateKeyByValue, localizeDate, openLink } from '@utils';
import { deleteCertificationUserTests } from '@api';
import { useAuth } from 'react-oidc-context';
import { CertificationType } from '@types';

type CertificationsTablePropsType = {
  certifications: CertificationType[]
  tableProps?: Partial<ITableProps<CertificationType>>
};
const CertificationsTable = ({ certifications, tableProps = {} }: CertificationsTablePropsType) => {
  const navigate = useNavigate();
  const { translate } = useAppTranslation();
  const { user } = useAuth();
  const params = useParams();
  const { open, isOpen, close } = useDisclosure();

  const [deleteTest, setDeleteTest] = useState<CertificationType | null>(null);

  useEffect(() => {
    deleteTest && open();
  }, [deleteTest]);

  const onDeleteCancel = () => {
    setDeleteTest(null);
    close();
  };

  const goToCertification = (idCertification: string) =>
    openLink(
      `${getWirkUrl('Form')}/contribute/certification/${idCertification}?token=${
        user.access_token
      }&tenant=${params.tenant}`,
    );

  const onDeleteConfirm = async () => {
    try {
      await deleteCertificationUserTests({
        id: deleteTest.idCertificationUserTest,
      });
      goToCertification(deleteTest.idCertification);
      setDeleteTest(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const tableColumns: ColumnsType<CertificationType> = useMemo(
    () => [
      {
        title: translate('common.name'),
        dataIndex: 'name',
        key: 'name',
        render: (name: CertificationType['name'], row) => (
          <Row align={'middle'}>
            <Space style={{ columnGap: 12, rowGap: 12 }}>
              <AvatarIcon
                icon={row.icon?.iconName as AvatarIcons}
                style={{ backgroundColor: row.icon?.iconColor }}
                size={'sm'}
              />
              <Text size="sm" color={colors.gray900} style={{ fontWeight: 500 }}>
                {name}
              </Text>
            </Space>
          </Row>
        ),
      },
      {
        title: translate('common.state'),
        dataIndex: 'state',
        key: 'state',
        render: (state: CertificationType['state']) => (
          <Row align={'middle'}>
            <PresetTag preset={'user-certification-state'} state={state} withDot>
              {translate(`common.states.userCertificationState.${state}`)}
            </PresetTag>
          </Row>
        ),
      },
      {
        title: translate('certifications.table.completionDate'),
        dataIndex: 'creationDate',
        key: 'creationDate',
        render: (creationDate: CertificationType['creationDate'], row) => (
          <Row align={'middle'}>
            {row.state !== getStateKeyByValue(UserCertificationState, 'new') &&
              localizeDate(creationDate)}
          </Row>
        ),
      },
      {
        title: translate('certifications.table.numberOfQuestions'),
        dataIndex: 'nbQuestions',
        key: 'nbQuestions',
        render: (nbQuestions: CertificationType['nbQuestions']) => (
          <Row align={'middle'}>{nbQuestions}</Row>
        ),
      },
      {
        title: translate('certifications.table.mark'),
        dataIndex: 'mark',
        key: 'mark',
        render: (mark: CertificationType['mark']) => (
          <Row align={'middle'} style={{ whiteSpace: 'nowrap' }}>
            {formatPercent(mark)}
          </Row>
        ),
      },
      {
        title: translate('certifications.table.actions'),
        key: 'actions',
        render: (_, row) => (
          <Row
            align={'middle'}
            css={css`
              gap: 4px;
            `}
          >
            {row.state === getStateKeyByValue(UserCertificationState, 'new') ||
            row.state === getStateKeyByValue(UserCertificationState, 'inProgress') ? (
                <IconButton
                  type={'text'}
                  icon="Share03"
                  onClick={() => goToCertification(row.idCertification)}
                />
              ) : (
                <>
                  {row.nbAttempts < row.nbAttemptsMax && (
                    <IconButton onClick={() => setDeleteTest(row)} type={'text'} icon="RefreshCW01" />
                  )}
                  <IconButton
                    onClick={() =>
                      navigate(routes.certification.navigate(row.idCertificationUserTest))
                    }
                    type={'text'}
                    icon="List"
                  />
                </>
              )}
          </Row>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Table data={certifications} rowKey="idCertification" columns={tableColumns} {...tableProps} />
      <ActionModal
        open={isOpen}
        title={translate('certificationTest.retakeCertificationTestTitle')}
        icon={'RefreshCW01'}
        iconColor={colors.warning600}
        iconBgColor={colors.warning100}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
        actionButtons={[
          {
            // TODO fix this
            // @ts-ignore
            text: translate('common.buttons.return'),
            onClick: onDeleteCancel,
          },
          {
            type: 'primary',
            // TODO fix this
            // @ts-ignore
            text: translate('common.buttons.confirm'),
            onClick: onDeleteConfirm,
          },
        ]}
      >
        <Text>{translate('certificationTest.retakeCertificationTestDescription')}</Text>
      </ActionModal>
    </>
  );
};

export default CertificationsTable;
